// src/EquipeManager.js
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://devis.takoussane.com/api';

const fmt = (s) => s || '—';

const callAdmin = async (endpoint, body) => {
  const res = await fetch(`${API_URL}/admin/${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erreur serveur');
  return data;
};

const EquipeManager = () => {
  const { profile } = useAuth();
  const [membres, setMembres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [toast, setToast] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);

  // Form nouveau membre
  const [form, setForm] = useState({ nom: '', email: '', password: '', role: 'commerciale', fonction: '', mobile: '' });
  const [formError, setFormError] = useState('');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchMembres = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('profiles').select('*').order('created_at', { ascending: true });
    setMembres(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchMembres(); }, [fetchMembres]);

  const handleCreate = async () => {
    if (!form.nom.trim() || !form.email.trim() || !form.password.trim()) {
      setFormError('Nom, email et mot de passe sont requis.');
      return;
    }
    if (form.password.length < 6) { setFormError('Mot de passe minimum 6 caractères.'); return; }
    const managers = membres.filter(m => m.role === 'manager');
    if (form.role === 'manager' && managers.length >= 3) { setFormError('Maximum 3 managers autorisés.'); return; }
    setSaving('create');
    try {
      await callAdmin('create-user', form);
      showToast(`✅ Compte créé pour ${form.nom}`);
      setForm({ nom: '', email: '', password: '', role: 'commerciale', fonction: '', mobile: '' });
      setFormError('');
      setShowForm(false);
      fetchMembres();
    } catch (e) { setFormError(e.message); }
    setSaving(null);
  };

  const handleDeactivate = async (m) => {
    setSaving(m.id + '-deact');
    try {
      await callAdmin('deactivate-user', { userId: m.id });
      showToast(`⏸️ ${m.nom} désactivé(e)`);
      fetchMembres();
    } catch (e) { showToast(e.message, 'error'); }
    setSaving(null);
  };

  const handleReactivate = async (m) => {
    setSaving(m.id + '-react');
    try {
      await callAdmin('reactivate-user', { userId: m.id });
      showToast(`✅ ${m.nom} réactivé(e)`);
      fetchMembres();
    } catch (e) { showToast(e.message, 'error'); }
    setSaving(null);
  };

  const handleDelete = async (m) => {
    setSaving(m.id + '-del');
    try {
      await callAdmin('delete-user', { userId: m.id });
      showToast(`🗑️ ${m.nom} supprimé(e) définitivement`);
      setConfirmDelete(null);
      fetchMembres();
    } catch (e) { showToast(e.message, 'error'); }
    setSaving(null);
  };

  const handleToggleRole = async (m) => {
    const newRole = m.role === 'manager' ? 'commerciale' : 'manager';
    const managers = membres.filter(mb => mb.role === 'manager');
    if (newRole === 'manager' && managers.length >= 3) { showToast('Maximum 3 managers autorisés.', 'error'); return; }
    setSaving(m.id + '-role');
    try {
      await callAdmin('update-role', { userId: m.id, role: newRole });
      showToast(`${m.nom} → ${newRole}`);
      fetchMembres();
    } catch (e) { showToast(e.message, 'error'); }
    setSaving(null);
  };

  const handleSendCredentials = async (m) => {
    const pwd = window.prompt(`Mot de passe temporaire à envoyer à ${m.nom} (${m.email}) :`);
    if (!pwd) return;
    setSendingEmail(m.id);
    try {
      await callAdmin('send-credentials', {
        to: m.email,
        nom: m.nom,
        email: m.email,
        password: pwd,
        from_name: profile?.nom || 'TAKOUSSANE ENERGY',
        from_email: profile?.email || 'contact@takoussane.com',
      });
      showToast(`📧 Identifiants envoyés à ${m.email}`);
    } catch (e) { showToast(e.message, 'error'); }
    setSendingEmail(null);
  };

  const initiales = (nom) => (nom || '?').split(' ').map(p => p[0]).join('').substring(0, 2).toUpperCase();
  const couleurRole = (role) => role === 'manager' ? '#7c3aed' : '#0284c7';

  const actifs = membres.filter(m => m.actif !== false);
  const inactifs = membres.filter(m => m.actif === false);

  return (
    <div style={{ position: 'relative' }}>
      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
          background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
          color: toast.type === 'error' ? '#dc2626' : '#166534',
          borderRadius: '10px', padding: '12px 18px', fontSize: '14px', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ fontSize: '13px', color: '#6b7280' }}>
          {actifs.length} membre{actifs.length > 1 ? 's' : ''} actif{actifs.length > 1 ? 's' : ''} · {membres.filter(m => m.role === 'manager').length}/3 managers
        </div>
        <button onClick={() => setShowForm(v => !v)}
          style={{ background: showForm ? '#e5e7eb' : '#1e40af', color: showForm ? '#374151' : 'white',
            border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px',
            fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}>
          {showForm ? '✕ Annuler' : '+ Nouveau membre'}
        </button>
      </div>

      {/* Formulaire création */}
      {showForm && (
        <div style={{ background: '#f8faff', border: '2px solid #c7d2fe', borderRadius: '12px',
          padding: '20px', marginBottom: '20px' }}>
          <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e40af', marginBottom: '14px' }}>
            👤 Nouveau membre de l'équipe
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {[
              { key: 'nom', label: 'Nom complet *', placeholder: 'Ex : AWA FAYE', type: 'text' },
              { key: 'email', label: 'Email *', placeholder: 'awa.faye@takoussane.com', type: 'email' },
              { key: 'password', label: 'Mot de passe temporaire *', placeholder: 'Min. 6 caractères', type: 'text' },
              { key: 'mobile', label: 'Mobile', placeholder: '+221 77 123 45 67', type: 'tel' },
              { key: 'fonction', label: 'Fonction', placeholder: 'Ex : Chargée de clientèle', type: 'text' },
            ].map(f => (
              <div key={f.key}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>{f.label}</label>
                <input type={f.type} value={form[f.key]} placeholder={f.placeholder}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #c7d2fe', borderRadius: '8px', padding: '9px 12px', fontSize: '13px', outline: 'none' }}
                  onFocus={e => e.target.style.borderColor = '#6366f1'}
                  onBlur={e => e.target.style.borderColor = '#c7d2fe'} />
              </div>
            ))}
            <div>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>Rôle</label>
              <select value={form.role} onChange={e => setForm(prev => ({ ...prev, role: e.target.value }))}
                style={{ width: '100%', border: '1px solid #c7d2fe', borderRadius: '8px', padding: '9px 12px', fontSize: '13px', outline: 'none', background: 'white' }}>
                <option value="commerciale">Commerciale</option>
                <option value="manager">Manager</option>
              </select>
            </div>
          </div>
          {formError && <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '10px' }}>⚠️ {formError}</p>}
          <button onClick={handleCreate} disabled={saving === 'create'}
            style={{ marginTop: '14px', background: saving === 'create' ? '#9ca3af' : '#1e40af', color: 'white',
              border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '13px',
              fontWeight: 600, cursor: 'pointer' }}>
            {saving === 'create' ? 'Création...' : '✅ Créer le compte'}
          </button>
        </div>
      )}

      {/* Liste membres actifs */}
      {loading ? (
        <div style={{ textAlign: 'center', color: '#9ca3af', padding: '30px' }}>Chargement...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {actifs.map(m => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '12px',
              background: 'white', borderRadius: '10px', padding: '12px 14px',
              border: '1px solid #e5e7eb', transition: 'box-shadow 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>

              {/* Avatar */}
              <div style={{ width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0,
                background: couleurRole(m.role) + '20', color: couleurRole(m.role),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '13px', fontWeight: 700, border: `2px solid ${couleurRole(m.role)}40` }}>
                {initiales(m.nom)}
              </div>

              {/* Infos */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: 700, fontSize: '14px', color: '#1f2937' }}>{m.nom}</span>
                  <span style={{ background: couleurRole(m.role) + '15', color: couleurRole(m.role),
                    border: `1px solid ${couleurRole(m.role)}30`, borderRadius: '999px',
                    padding: '1px 8px', fontSize: '11px', fontWeight: 600 }}>
                    {m.role === 'manager' ? '👑 Manager' : '💼 Commerciale'}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                  {m.email}{m.fonction ? ' · ' + m.fonction : ''}{m.mobile ? ' · ' + m.mobile : ''}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                {/* Email identifiants */}
                <button onClick={() => handleSendCredentials(m)} disabled={sendingEmail === m.id}
                  title="Envoyer identifiants par email"
                  style={{ background: '#f0f9ff', color: '#0284c7', border: '1px solid #bae6fd',
                    borderRadius: '6px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 500 }}>
                  {sendingEmail === m.id ? '...' : '📧'}
                </button>
                {/* Changer rôle */}
                {m.id !== profile?.id && (
                  <button onClick={() => handleToggleRole(m)} disabled={saving === m.id + '-role'}
                    title={m.role === 'manager' ? 'Rétrograder en commerciale' : 'Promouvoir en manager'}
                    style={{ background: m.role === 'manager' ? '#fef3c7' : '#f5f3ff',
                      color: m.role === 'manager' ? '#d97706' : '#7c3aed',
                      border: `1px solid ${m.role === 'manager' ? '#fde68a' : '#ddd6fe'}`,
                      borderRadius: '6px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 500 }}>
                    {saving === m.id + '-role' ? '...' : m.role === 'manager' ? '⬇️ Rétrograder' : '⬆️ Manager'}
                  </button>
                )}
                {/* Désactiver */}
                {m.id !== profile?.id && (
                  <button onClick={() => handleDeactivate(m)} disabled={saving === m.id + '-deact'}
                    title="Désactiver le compte"
                    style={{ background: '#fef9c3', color: '#a16207', border: '1px solid #fde047',
                      borderRadius: '6px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 500 }}>
                    {saving === m.id + '-deact' ? '...' : '⏸️ Désactiver'}
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Membres inactifs */}
          {inactifs.length > 0 && (
            <>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase',
                letterSpacing: '0.05em', marginTop: '12px', marginBottom: '4px' }}>
                Comptes désactivés ({inactifs.length})
              </div>
              {inactifs.map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '12px',
                  background: '#f9fafb', borderRadius: '10px', padding: '10px 14px',
                  border: '1px solid #e5e7eb', opacity: 0.7 }}>
                  <div style={{ width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                    background: '#f3f4f6', color: '#9ca3af', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: '12px', fontWeight: 700, border: '2px solid #e5e7eb' }}>
                    {initiales(m.nom)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '13px', color: '#6b7280', textDecoration: 'line-through' }}>{m.nom}</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>{m.email}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => handleReactivate(m)} disabled={saving === m.id + '-react'}
                      style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac',
                        borderRadius: '6px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 500 }}>
                      {saving === m.id + '-react' ? '...' : '▶️ Réactiver'}
                    </button>
                    <button onClick={() => setConfirmDelete(m)}
                      style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5',
                        borderRadius: '6px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 500 }}>
                      🗑️ Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Modal confirmation suppression */}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998 }}>
          <div style={{ background: 'white', borderRadius: '14px', padding: '28px', maxWidth: '400px', width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#dc2626', marginBottom: '10px' }}>⚠️ Suppression définitive</div>
            <p style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6 }}>
              Vous allez supprimer définitivement le compte de <strong>{confirmDelete.nom}</strong> ({confirmDelete.email}).
              Cette action est <strong>irréversible</strong> — les devis associés seront conservés.
            </p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDelete(null)}
                style={{ background: '#f3f4f6', border: 'none', borderRadius: '8px', padding: '9px 18px', fontSize: '13px', cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={() => handleDelete(confirmDelete)} disabled={saving === confirmDelete.id + '-del'}
                style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 18px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                {saving === confirmDelete.id + '-del' ? 'Suppression...' : '🗑️ Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipeManager;
